import { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Send, X, Loader2, ImagePlus, User, ChevronDown, ChevronUp, PenSquare } from 'lucide-react';
import { feedService } from '../services/feedService';

interface PostingCardProps {
  onPostCreated: () => void;
}

interface PostData {
  title: string;
  content: string;
  author: string;
  image_url?: string;
}

export function PostingCard({ onPostCreated }: PostingCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [adminKey, setAdminKey] = useState('');
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        setError('Please upload an image file');
        return;
      }

      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        setError('Image size should be less than 5MB');
        return;
      }

      try {
        const reader = new FileReader();
        reader.onloadend = () => {
          const result = reader.result as string;
          // Verify we have a complete base64 string
          if (result.startsWith('data:image/') && result.includes('base64,')) {
            setImageUrl(result);
            setError('');
          } else {
            setError('Invalid image format');
          }
        };
        reader.readAsDataURL(file);
      } catch (err) {
        console.error('Error reading file:', err);
        setError('Failed to process image');
      }
    }
  };

  const handleRemoveImage = () => {
    setImageUrl(null);
    setError('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim() || !content.trim() || !adminKey.trim()) {
      setError('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const postData: PostData = {
        title,
        content,
        author: adminKey
      };

      if (imageUrl && imageUrl.startsWith('data:image/')) {
        postData.image_url = imageUrl;
      }

      const response = await feedService.createPost(postData, adminKey);
      
      if (response.success) {
        setTitle('');
        setContent('');
        setAdminKey('');
        setImageUrl(null);
        setIsExpanded(false);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        onPostCreated();
      } else {
        setError(response.message || 'Failed to create post');
      }
    } catch (err) {
      console.error('Error creating post:', err);
      setError('Failed to create post. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-100 dark:border-gray-700 mb-6 w-full max-w-2xl mx-auto overflow-hidden"
    >
      {/* Minimized View */}
      <div
        onClick={() => setIsExpanded(!isExpanded)}
        className="p-4 cursor-pointer flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center">
            <PenSquare className="w-5 h-5 text-blue-600 dark:text-blue-300" />
          </div>
          <span className="font-medium text-gray-700 dark:text-gray-200">
            {isExpanded ? "Close post editor" : "Create a new post"}
          </span>
        </div>
        {isExpanded ? (
          <ChevronUp className="w-5 h-5 text-gray-500" />
        ) : (
          <ChevronDown className="w-5 h-5 text-gray-500" />
        )}
      </div>

      {/* Expanded Form */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <div className="p-4 border-t border-gray-100 dark:border-gray-700">
              <form onSubmit={handleSubmit} className="space-y-4">
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Title"
                  className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-700 rounded-xl border border-gray-200 dark:border-gray-600 outline-none focus:ring-2 focus:ring-blue-500 dark:text-white text-lg font-medium transition-all duration-200"
                  disabled={isSubmitting}
                />

                <textarea
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  placeholder="What's on your mind?"
                  rows={3}
                  className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-700 rounded-xl border border-gray-200 dark:border-gray-600 outline-none focus:ring-2 focus:ring-blue-500 dark:text-white resize-none transition-all duration-200"
                  disabled={isSubmitting}
                />

                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Image Upload
                    </label>
                    {imageUrl && (
                      <button
                        type="button"
                        onClick={handleRemoveImage}
                        className="text-red-500 hover:text-red-600 text-sm font-medium"
                      >
                        Remove Image
                      </button>
                    )}
                  </div>

                  <div className="relative">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      className="hidden"
                      id="image-upload"
                      ref={fileInputRef}
                      disabled={isSubmitting}
                    />
                    <label
                      htmlFor="image-upload"
                      className="cursor-pointer block w-full px-4 py-3 bg-gray-50 dark:bg-gray-700 rounded-xl border-2 border-dashed border-gray-300 dark:border-gray-600 hover:border-blue-500 dark:hover:border-blue-400 transition-all duration-200"
                    >
                      <div className="flex items-center justify-center">
                        <ImagePlus className="w-6 h-6 text-gray-400 dark:text-gray-500 mr-2" />
                        <span className="text-gray-600 dark:text-gray-400">
                          {imageUrl ? 'Change Image' : 'Upload Image'}
                        </span>
                      </div>
                    </label>
                  </div>

                  {imageUrl && (
                    <div className="relative rounded-xl overflow-hidden bg-gray-100 dark:bg-gray-700">
                      <img
                        src={imageUrl}
                        alt="Preview"
                        className="w-full object-cover rounded-xl"
                        style={{ maxHeight: '300px' }}
                      />
                    </div>
                  )}
                </div>

                <div className="space-y-4">
                  <input
                    type="password"
                    value={adminKey}
                    onChange={(e) => setAdminKey(e.target.value)}
                    placeholder="Administrator"
                    className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-700 rounded-xl border border-gray-200 dark:border-gray-600 outline-none focus:ring-2 focus:ring-blue-500 dark:text-white transition-all duration-200"
                    disabled={isSubmitting}
                  />

                  {error && (
                    <p className="text-red-500 text-sm mt-2">{error}</p>
                  )}

                  <div className="flex justify-end pt-4 border-t border-gray-200 dark:border-gray-700">
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin" />
                          Posting...
                        </>
                      ) : (
                        <>
                          <Send className="w-5 h-5" />
                          Post
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}